import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBell,
  FaChevronDown,
  FaUser,
  FaCog,
  FaSignOutAlt,
  FaSearch,
} from "react-icons/fa";

import authService from "../services/authService";
import { BRANDING_CHANGED_EVENT, getStoredBranding, loadCompanyBranding } from "../utils/companyBranding";

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

/* ─────────────────────────────────────────
   TOPBAR
───────────────────────────────────────── */
const Topbar = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [branding, setBranding] = useState(getStoredBranding);

  const user = authService.getCurrentUser();
  const initials = getInitials(user?.name);

  useEffect(() => {
    loadCompanyBranding().then(setBranding).catch(() => {});
    const refreshBranding = () => setBranding(getStoredBranding());
    window.addEventListener(BRANDING_CHANGED_EVENT, refreshBranding);
    return () => window.removeEventListener(BRANDING_CHANGED_EVENT, refreshBranding);
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  return (
    <header className="cb-topbar h-16 flex items-center justify-between px-6 flex-shrink-0 z-10">

      {/* ── Left ── */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative">
          <FaSearch
            size={11}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search devices, users…"
            className="
              pl-9 pr-4 py-2 text-[13px] rounded-xl w-72
              bg-white/75 border border-slate-200/80 text-slate-700
              placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500
              transition-all
            "
          />
        </div>

        {/* Live / Offline status */}
        <div className="cb-chip px-3 py-1.5 text-[11px]">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[11px] font-semibold text-green-700 tracking-wide">Live</span>
        </div>
      </div>

      {/* ── Right ── */}
      <div className="flex items-center gap-2">

        {/* Notification bell */}
        <div className="relative">
          <button className="
            w-8 h-8 flex items-center justify-center rounded-lg
            bg-white/75 border border-slate-200/80 text-slate-500
            hover:bg-white hover:text-slate-800 hover:border-slate-300
            transition-colors
          ">
            <FaBell size={13} />
          </button>
          <span className="
            absolute -top-1 -right-1 w-4 h-4
            bg-red-500 text-white text-[9px] font-bold
            rounded-full flex items-center justify-center
            border-2 border-white
          ">
            3
          </span>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 mx-1" />

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen(!open)}
            className="
              flex items-center gap-2.5 pl-1.5 pr-2.5 py-1.5
              rounded-xl border border-slate-200/80 bg-white/75
              hover:bg-white hover:border-slate-300
              transition-colors cursor-pointer
            "
          >
            {/* Avatar */}
            <div className="
              w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0
              bg-slate-900 text-white text-[10px] font-bold
            ">
              {branding?.logo_data ? <img src={branding.logo_data} alt={`${branding.company_name || "Company"} logo`} className="h-full w-full rounded-md object-contain bg-white p-0.5" /> : initials}
            </div>

            {/* Name + role */}
            <div className="text-left leading-tight">
              <div className="text-[12px] font-semibold text-gray-800 whitespace-nowrap">
                {user?.name || "User"}
              </div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wide">
                {user?.role || "role"}
              </div>
            </div>

            <FaChevronDown
              size={9}
              className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            />
          </button>

          {/* Dropdown */}
          {open && (
            <div className="
              absolute right-0 mt-2 w-48
              bg-white border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/10
              overflow-hidden z-50
              animate-in fade-in slide-in-from-top-1 duration-150
            ">
              {/* User header */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-[12px] font-semibold text-gray-800 truncate">
                  {user?.name || "User"}
                </p>
                <p className="text-[11px] text-gray-400 truncate">
                  {user?.email || ""}
                </p>
              </div>

              <div className="py-1">
                <DropdownItem
                  icon={FaUser}
                  label="Profile"
                  onClick={() => { navigate("/dashboard/profile"); setOpen(false); }}
                />
                <DropdownItem
                  icon={FaCog}
                  label="Settings"
                  onClick={() => { navigate("/dashboard/settings"); setOpen(false); }}
                />
              </div>

              <div className="border-t border-gray-100 py-1">
                <DropdownItem
                  icon={FaSignOutAlt}
                  label="Logout"
                  onClick={handleLogout}
                  danger
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

/* ─────────────────────────────────────────
   DROPDOWN ITEM
───────────────────────────────────────── */
const DropdownItem = ({ icon: Icon, label, onClick, danger = false }) => (
  <button
    onClick={onClick}
    className={`
      w-full flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium
      transition-colors text-left
      ${danger
        ? "text-red-500 hover:bg-red-50"
        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      }
    `}
  >
    <Icon size={12} className={danger ? "text-red-400" : "text-gray-400"} />
    {label}
  </button>
);

export default Topbar;
